import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package, ClipboardList
} from 'lucide-react';

export function SupplierManagementPanel() {
  const [settings, setSettings] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // VRM Document Types state
  const [docTypes, setDocTypes] = useState([]);
  const [docSearch, setDocSearch] = useState('');
  const [docSort, handleDocSort] = useSortState('name', 'asc');
  const [creditSort, handleCreditSort] = useSortState('', 'asc');
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [newDoc, setNewDoc] = useState({ name: '', description: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, docRes] = await Promise.all([
        api.get('/api/admin/supplier-management-settings'),
        api.get('/api/admin/vrm-document-types')
      ]);
      setSettings(settingsRes.data.settings || getDefaults());
      setEligibleUsers(settingsRes.data.eligible_users || []);
      setDocTypes(docRes.data.document_types || []);
    } catch (err) { setSettings(getDefaults()); }
    setLoading(false);
  };

  const getDefaults = () => ({
    supplier_approver: '', activate_supplier_update: false, supplier_manager: '',
    vendor_risk_manager: '', threshold_rating: 3, activate_credit_score_report: false,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/supplier-management-settings', settings);
      toast.success('Supplier management settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const update = (field, value) => setSettings(p => ({ ...p, [field]: value }));

  const handleCreateDoc = async () => {
    if (!newDoc.name) { toast.error('Name is required'); return; }
    try {
      await api.post('/api/admin/vrm-document-types', newDoc);
      toast.success('Document type created');
      setNewDoc({ name: '', description: '' });
      setShowAddDoc(false);
      const res = await api.get('/api/admin/vrm-document-types');
      setDocTypes(res.data.document_types || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const handleDeleteDoc = async (name) => {
    try {
      await api.delete(`/api/admin/vrm-document-types/${name}`);
      toast.success('Document type deleted');
      setDocTypes(prev => prev.filter(d => d.name !== name));
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleSearchDocs = async () => {
    try {
      const res = await api.get('/api/admin/vrm-document-types', { params: { search: docSearch } });
      setDocTypes(res.data.document_types || []);
    } catch (err) { console.error(err); }
  };

  if (loading) return <LoadingSpinner />;

  const portalUrl = `${window.location.origin}/supplier-portal`;
  const tendersUrl = `${window.location.origin}/public_RFX`;

  return (
    <div className="max-w-4xl" data-testid="supplier-management-panel">
      <h1 className="text-2xl font-bold mb-1">Supplier Management</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Supplier Settings</p>

      {/* Section 1: Portal URLs & Email Settings */}
      <div className="border rounded-lg p-6 mb-6" data-testid="sm-external-supplier-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Supplier Portal
        </h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-52 text-sm flex-shrink-0">Supplier Portal Login</Label>
            <code className="text-xs text-primary bg-muted/30 px-2 py-1 rounded truncate max-w-lg" data-testid="sm-portal-url">{portalUrl}</code>
            <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success('Copied!'); }}>
              <ClipboardList className="h-3 w-3" />
            </Button>
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Open</a>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-52 text-sm flex-shrink-0">Public Sourcing Events</Label>
            <code className="text-xs text-primary bg-muted/30 px-2 py-1 rounded truncate max-w-lg" data-testid="sm-tenders-url">{tendersUrl}</code>
            <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(tendersUrl); toast.success('Copied!'); }}>
              <ClipboardList className="h-3 w-3" />
            </Button>
            <a href={tendersUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Open</a>
          </div>
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center gap-4 mb-3">
              <Label className="w-52 text-sm flex-shrink-0">Send Approval Emails</Label>
              <div className="flex items-center gap-2">
                <Switch checked={settings.send_approval_emails !== false} onCheckedChange={v => update('send_approval_emails', v)} data-testid="sm-send-approval-emails" />
                <span className="text-xs text-muted-foreground">{settings.send_approval_emails !== false ? 'Yes — vendor managers receive email when suppliers register' : 'No — in-app notifications only'}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Label className="w-52 text-sm flex-shrink-0">Legacy Approver</Label>
              <select value={settings.supplier_approver || ''} onChange={e => update('supplier_approver', e.target.value)}
                className="w-52 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="sm-supplier-approver">
                <option value="">-- None (use Vendor Managers) --</option>
                {eligibleUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
              <p className="text-xs text-muted-foreground flex-1">Optional fallback. Set users as Vendor Manager on User Maintenance instead.</p>
            </div>
          </div>
        </div>
        {/* Item / Price Approval */}
        <div className="border-t mt-6 pt-4">
          <h4 className="text-sm font-semibold mb-4">Item / Price Approval</h4>
          <div className="flex items-start gap-4 mb-4">
            <Label className="w-52 text-sm pt-1 flex-shrink-0">Activate Supplier Update</Label>
            <div className="flex items-center gap-2">
              <Switch checked={settings.activate_supplier_update} onCheckedChange={v => update('activate_supplier_update', v)} data-testid="sm-activate-supplier-update" />
              <span className={`text-xs font-medium ${settings.activate_supplier_update ? 'text-green-600' : 'text-muted-foreground'}`}>
                {settings.activate_supplier_update ? 'Yes' : 'No'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground flex-1">If switched to 'Yes', an email will be sent to the chosen Supplier Manager when a Supplier updates price on the Supplier Portal.</p>
          </div>
          <div className="flex items-start gap-4">
            <Label className="w-52 text-sm pt-1 flex-shrink-0">Supplier Manager</Label>
            <select value={settings.supplier_manager || ''} onChange={e => update('supplier_manager', e.target.value)}
              className="w-52 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="sm-supplier-manager">
              <option value="">-- Select --</option>
              {eligibleUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground flex-1">The Supplier Manager can log in to accept/reject the updated price or approve/reject the item via Data Files {'>'} Items {'>'} Item Supplier Approval.</p>
          </div>
        </div>
      </div>

      {/* Section 2: VRM Settings */}
      <div className="border rounded-lg p-6 mb-6" data-testid="sm-vrm-settings-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          VRM Settings
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          <strong>Note:</strong> Here you can select the Vendor Risk Manager and then this manager will be able to approve documents uploaded by the suppliers. If the manager assesses a document below the threshold value then that document will be rejected back to the supplier.
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-40 text-sm">Vendor Risk Manager</Label>
            <select value={settings.vendor_risk_manager || ''} onChange={e => update('vendor_risk_manager', e.target.value)}
              className="w-52 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="sm-vendor-risk-manager">
              <option value="">-- Select --</option>
              {eligibleUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-40 text-sm">Threshold Rating</Label>
            <select value={settings.threshold_rating || 3} onChange={e => update('threshold_rating', parseInt(e.target.value))}
              className="w-24 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="sm-threshold-rating">
              {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Section 3: VRM Document Type */}
      <div className="border rounded-lg p-6 mb-6" data-testid="sm-vrm-document-type-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          VRM Document Type
        </h3>
        <div className="flex items-center gap-2 mb-4">
          <Input value={docSearch} onChange={e => setDocSearch(e.target.value)} placeholder="Search" className="w-40" data-testid="sm-vrm-doc-search" />
          <Button size="sm" onClick={handleSearchDocs}><Search className="h-3 w-3 mr-1" /> Search</Button>
          <Button size="sm" variant="outline" onClick={() => { setDocSearch(''); fetchAll(); }}><X className="h-3 w-3 mr-1" /> Clear</Button>
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground">Total records: {docTypes.length}</span>
          <Button size="sm" variant="outline" onClick={() => setShowAddDoc(true)} data-testid="sm-add-doc-type-btn">
            <Plus className="h-3 w-3 mr-1" /> Add New
          </Button>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <SortableTableHeader label="Name" field="name" sortState={docSort} onSort={handleDocSort} />
                <SortableTableHeader label="Description" field="description" sortState={docSort} onSort={handleDocSort} />
                <SortableTableHeader label="Active" field="active" sortState={docSort} onSort={handleDocSort} className="text-center" />
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {docTypes.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground text-xs">No document types found</td></tr>
              ) : sortData(docTypes, docSort.field, docSort.dir).map(d => (
                <tr key={d.name} className="border-t hover:bg-accent/50">
                  <td className="px-3 py-2">{d.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{d.description}</td>
                  <td className="px-3 py-2 text-center">{d.is_active ? 'Yes' : 'No'}</td>
                  <td className="px-2">
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteDoc(d.name)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Add Document Type Dialog */}
        <Dialog open={showAddDoc} onOpenChange={setShowAddDoc}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add VRM Document Type</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Name *</Label>
                <Input value={newDoc.name} onChange={e => setNewDoc(p => ({ ...p, name: e.target.value }))} data-testid="sm-new-doc-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Input value={newDoc.description} onChange={e => setNewDoc(p => ({ ...p, description: e.target.value }))} data-testid="sm-new-doc-desc" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDoc(false)}>Cancel</Button>
              <Button onClick={handleCreateDoc}><Plus className="h-4 w-4 mr-1" /> Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Section 4: Credit Score Report */}
      <div className="border rounded-lg p-6 mb-6" data-testid="sm-credit-score-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">4</span>
          Credit Score Report
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          By enabling the 'Credit Score Report' feature, the system will allow the purchase of a specified number of credit score reports for suppliers.
          Once the system administrator completes the purchase, users can generate supplier credit score reports in the main application.
        </p>
        <div className="flex items-center gap-4 mb-6">
          <Label className="w-52 text-sm">Activate Credit Score Report</Label>
          <Switch checked={settings.activate_credit_score_report} onCheckedChange={v => update('activate_credit_score_report', v)} data-testid="sm-activate-credit-score" />
          <span className={`text-xs font-medium ${settings.activate_credit_score_report ? 'text-green-600' : 'text-muted-foreground'}`}>
            {settings.activate_credit_score_report ? 'Yes' : 'No'}
          </span>
        </div>
        {settings.activate_credit_score_report && (
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold mb-2">Credit Score Report</h4>
            <p className="text-xs text-muted-foreground mb-4">Please select the number of credit score reports from the dropdown to purchase, in multiples of 10.</p>
            <div className="flex items-center gap-3 mb-6">
              <Label className="text-xs">Select the Number of Credit Score Reports</Label>
              <select className="w-24 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="sm-credit-score-count">
                <option value="">Select</option>
                {[10, 20, 30, 40, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <Button size="sm" data-testid="sm-credit-score-pay-btn">Pay</Button>
            </div>
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Purchase History</h4>
                <span className="text-xs text-muted-foreground">Total Record: 0</span>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <SortableTableHeader label="Transaction No" field="transaction_no" sortState={creditSort} onSort={handleCreditSort} />
                      <SortableTableHeader label="Purchased Count" field="purchased_count" sortState={creditSort} onSort={handleCreditSort} className="text-center" />
                      <SortableTableHeader label="Used Count" field="used_count" sortState={creditSort} onSort={handleCreditSort} className="text-center" />
                      <SortableTableHeader label="Payment ID" field="payment_id" sortState={creditSort} onSort={handleCreditSort} />
                      <SortableTableHeader label="Payment Date" field="payment_date" sortState={creditSort} onSort={handleCreditSort} />
                      <SortableTableHeader label="Payment Status" field="payment_status" sortState={creditSort} onSort={handleCreditSort} />
                      <SortableTableHeader label="Payment Amount" field="payment_amount" sortState={creditSort} onSort={handleCreditSort} className="text-right" />
                      <SortableTableHeader label="Active" field="active" sortState={creditSort} onSort={handleCreditSort} className="text-center" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">No purchase history</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="supplier-management-save-btn">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Update'}
        </Button>
      </div>
    </div>
  );
}
